import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * HB 2026-10-20 (round 77): a small state panel for the phone menu, only with ?hbmenu=1 on the address (kept for the
 * visit in sessionStorage; ?hbmenu=0 ends it). It shows what the X and the drawer follow, and the last events, so a
 * phone's own behavior can be read from a screenshot. Never shown without the flag.
 */
const hbMenuLines = [];
let hbMenuBox = null;
function hbMenuOn() {
  try {
    const q = new URLSearchParams(location.search).get('hbmenu');
    if (q === '1') sessionStorage.setItem('hb-menu-debug', '1');
    if (q === '0') sessionStorage.removeItem('hb-menu-debug');
    return sessionStorage.getItem('hb-menu-debug') === '1';
  } catch (e) {
    return false;
  }
}
function hbMenuLog(line) {
  if (!hbMenuBox) return;
  hbMenuLines.push(Math.round(performance.now()) + ' ' + line);
  while (hbMenuLines.length > 8) hbMenuLines.shift();
}
function hbMenuDebug(drawerEl) {
  if (hbMenuBox || !hbMenuOn()) return;
  hbMenuBox = document.createElement('div');
  hbMenuBox.setAttribute('aria-hidden', 'true');
  hbMenuBox.style.cssText =
    'position:fixed;left:6px;bottom:6px;z-index:2147483647;max-width:70vw;padding:6px 8px;background:rgba(0,0,0,.78);color:#fff;font:10px/1.35 monospace;white-space:pre;pointer-events:none;border-radius:4px';
  document.body.appendChild(hbMenuBox);
  const summary = drawerEl.querySelector('summary');
  ['pointerdown', 'touchend', 'click'].forEach((type) => {
    window.addEventListener(
      type,
      (event) => {
        if (summary && event.target instanceof Node && summary.contains(event.target)) hbMenuLog(type);
      },
      true
    );
  });
  const paint = () => {
    const details = drawerEl.querySelector('details');
    const panel = details && details.querySelector(':scope > .menu-drawer');
    let s = 'menu ' + (details ? '' : 'no details');
    if (details) {
      const cs = panel ? getComputedStyle(panel) : null;
      const r = panel ? panel.getBoundingClientRect() : null;
      s +=
        'open=' + details.hasAttribute('open') + ' class=' + details.classList.contains('menu-open') + ' js=' + details.hasAttribute('data-hb-js') +
        '\ndrawer ' + (cs ? cs.visibility + ' ' + cs.transform.replace(/\s/g, '') : '-') +
        '\nbox ' + (r ? [r.left, r.top, r.width, r.height].map(Math.round).join(',') : '-') + ' vh=' + innerHeight;
    }
    // round 78: the viewport as the phone counts it (small, large, dynamic, visual) and the open sheet's box
    const probe = (unit) => {
      const el = document.createElement('span'); // a span: the theme hides empty divs
      el.style.cssText = 'display:block;position:fixed;top:0;left:-9px;width:1px;visibility:hidden;pointer-events:none;height:100' + unit;
      document.body.appendChild(el);
      const h = el.offsetHeight;
      el.remove();
      return h;
    };
    const vv = window.visualViewport;
    s += '\nview s/l/d=' + probe('svh') + '/' + probe('lvh') + '/' + probe('dvh') + ' vv=' + (vv ? Math.round(vv.height) : '-');
    const dlg = document.querySelector('dialog.hb-sl[open]');
    if (dlg) {
      const dr = dlg.getBoundingClientRect();
      const img = dlg.querySelector('.hb-sb__bgimg.is-on, .hb-sl__growimg');
      const ir = img ? img.getBoundingClientRect() : null;
      s += '\nsheet ' + Math.round(dr.top) + ',' + Math.round(dr.height) + (ir ? ' pic ' + Math.round(ir.top) + ',' + Math.round(ir.height) : '');
    }
    hbMenuBox.textContent = s + '\n' + hbMenuLines.join('\n');
  };
  paint();
  setInterval(paint, 250);
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * HB 2026-10-14 (round 50, Vic: "When I clicked all apparel in Mobile menu, I had to click 3 times"): a close
   * cleaned up when its slide-out ended; a close followed by an open soon after let that late clean-up undo the new
   * open (the menu flashed and shut, the next tap "closed" the shut menu, the third opened it). Every open and close
   * now takes a number, and a clean-up only runs while its close is still the latest.
   */
  #seq = 0;

  /**
   * HB 2026-10-20 (round 77, Vic on his iPhone in Chrome: "I could not open the menu. It just shows the X at top like
   * menu would be open, but menu does not fold out on my screen"): the X follows the details' open attribute and the
   * drawer the menu-open class, so anything that set the one without the other left the X up and the drawer away.
   * The two are now kept together whoever moves them: the class goes on in the same moment as the attribute (a forced
   * layout between them keeps the slide), the details' own toggle event brings the drawer in or out to match the
   * attribute (so the browser's own summary toggle, on iOS too, can never leave the X alone), a toggle that undoes our
   * own tap a moment later is put back, and a close finishes after the slide even if no animation reports its end.
   * The details carries data-hb-js while this script runs; without it, assets/hb-header.css shows the drawer whenever
   * the details is open. ?hbmenu=1 on a preview address shows a small state panel (debug only, never for customers).
   */
  #intent = ''; // 'open' or 'closed': what our own tap or call decided last
  #intentAt = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    const details = this.refs.details || this.querySelector('details');
    if (details) {
      details.setAttribute('data-hb-js', '');
      details.addEventListener('toggle', this.#onToggle);
    }
    hbMenuDebug(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    const details = this.refs.details || this.querySelector('details');
    if (details) details.removeEventListener('toggle', this.#onToggle);
  }

  #decide(state) {
    this.#intent = state;
    this.#intentAt = performance.now();
  }

  /**
   * The details' toggle event (round 77): the drawer follows the open attribute, unless our own tap decided a moment
   * ago, in which case the attribute is put back to what the tap decided.
   */
  #onToggle = () => {
    const details = this.refs.details;
    if (!details) return;
    const isOpen = details.hasAttribute('open');
    const shown = details.classList.contains('menu-open');
    hbMenuLog('toggle ' + (isOpen ? 'open' : 'closed') + (shown ? ' +class' : ''));
    if (this.#intent && performance.now() - this.#intentAt < 500) {
      // the browser's own summary toggle undid our open while the drawer is in: open it again (a close of ours takes
      // the class off first, so it never comes here)
      if (this.#intent === 'open' && !isOpen && shown) details.setAttribute('open', '');
      return;
    }
    if (isOpen && !shown) this.open();
    else if (!isOpen && shown) this.#close(details);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * HB 2026-10-14 (round 50): decided by what she sees (the menu-open class), not by the details' open attribute,
   * which the close button and Escape left behind; the summary's own toggle is held back and the attribute is set here
   * on opening and taken off once the menu has slid out, so the two never disagree.
   * @param {Event} [event]
   */
  toggle(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    hbMenuLog('tap' + (event && event.defaultPrevented ? ' (default held)' : ''));
    return this.refs.details.classList.contains('menu-open') ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#seq += 1; // a close still sliding out no longer cleans up after this open (round 50)
    if (details === this.refs.details) this.#decide('open');
    if (details === this.refs.details && !details.hasAttribute('open')) details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    if (details !== this.refs.details) {
      // a submenu: its own summary opens it after this click, so its slide starts on the next frame (as before)
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      });
      return;
    }
    // round 77: the main drawer's class goes on now, not on the next frame, so the X never stands alone; reading the
    // panel's size first lays it out at its closed place, so it still slides in
    const panel = details.querySelector(':scope > .menu-drawer');
    if (panel) void panel.offsetWidth;
    details.classList.add('menu-open');
    hbMenuLog('open');
    setTimeout(() => {
      trapFocus(details);
    }, 0);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#decide('closed');
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
   #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Release focus trap immediately for the MAIN drawer.
    if (details === this.refs.details) {
      removeTrapFocus();
    }

    const seq = (this.#seq += 1);
    if (details === this.refs.details) this.#decide('closed'); // Escape and a followed toggle close too
    hbMenuLog('close');
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      // HB 2026-10-14 (round 50): an open (or another close) came after this close: leave it be
      if (seq !== this.#seq) return;
      reset(details);

      if (details === this.refs.details) {
        // the menu has slid out: the details closes too (round 50; the summary's own toggle is held back in toggle())
        details.removeAttribute('open');
        // Call again as safe cleanup.
        removeTrapFocus();

        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details,[custom_open])');

        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    };
    onAnimationEnd(details, finish);
    // round 77: the clean-up runs after the slide even if an animation never reports its end
    setTimeout(finish, 700);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  //element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}